import React, { useState, useEffect, useCallback } from "react";
import { X, TrendingUp, Package, Users, ShoppingCart, Store, DollarSign, Clock, AlertTriangle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios, { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, fmtDate, fmtTime } from "@/utils/dateVE";

export default function TrialConversionBanner() {
  const { t } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const shouldShow = useCallback(() => {
    if (!user?.tenant_id) return false;
    const key = `trial_banner_dismissed_${user.tenant_id}`;
    const ts = localStorage.getItem(key);
    if (ts && Date.now() - parseInt(ts) < 86400000) return false;
    return true;
  }, [user]);

  useEffect(() => {
    if (!shouldShow()) return;
    axios.get(`${API}/tenant/my-usage`).then(r => {
      const d = r.data;
      const needsBanner = d.is_trial || d.subscription_status === "expiring_soon" || d.subscription_status === "expired";
      if (needsBanner) setData(d);
    }).catch(() => {});
  }, [shouldShow]);

  if (dismissed || !data) return null;

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(`trial_banner_dismissed_${user.tenant_id}`, Date.now().toString());
  };

  const isTrial = data.is_trial;
  const isExpired = data.subscription_status === "expired" || data.trial_expired;
  const isExpiring = data.subscription_status === "expiring_soon" || (isTrial && data.trial_days_left <= 7);
  const daysLeft = isTrial ? data.trial_days_left : data.subscription_days_left;

  const gradient = isExpired
    ? "from-red-600/20 via-red-500/10 to-slate-900"
    : isExpiring
    ? "from-amber-600/20 via-amber-500/10 to-slate-900"
    : "from-blue-600/20 via-blue-500/10 to-slate-900";

  const borderColor = isExpired ? "border-red-500/40" : isExpiring ? "border-amber-500/40" : "border-blue-500/40";
  const accentColor = isExpired ? "text-red-400" : isExpiring ? "text-amber-400" : "text-blue-400";

  const stats = [
    { icon: Package, label: t('trialProducts'), value: data.products },
    { icon: Users, label: t('trialCustomers'), value: data.customers },
    { icon: ShoppingCart, label: t('trialSales'), value: data.sales },
    { icon: DollarSign, label: t('trialRevenue'), value: `$${data.total_revenue?.toLocaleString()}` },
    { icon: Store, label: t('trialStores'), value: data.stores },
  ];

  return (
    <div className={`relative rounded-xl border ${borderColor} bg-gradient-to-r ${gradient} mb-4 overflow-hidden`} data-testid="trial-conversion-banner">
      {/* Decorative corner */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/5 to-transparent rounded-bl-full pointer-events-none" />

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${isExpired ? 'bg-red-500/20' : isExpiring ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
              {isExpired ? <AlertTriangle className={`w-5 h-5 ${accentColor}`} /> : <Clock className={`w-5 h-5 ${accentColor}`} />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-white font-semibold text-sm">
                  {isTrial ? t('trialTitle') : t('trialSubTitle')}
                </h3>
                <Badge className={`text-xs ${isExpired ? 'bg-red-500/20 text-red-400' : isExpiring ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                  {isExpired ? t('trialExpiredBadge') : `${Math.abs(daysLeft)}${t('acDayUnit')}${daysLeft >= 0 ? t('trialRemaining') : t('trialOverdue')}`}
                </Badge>
              </div>
              <p className="text-slate-400 text-xs mt-0.5">
                {isExpired ? t('trialExpiredMsg') : t('trialExpiringMsg')}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={dismiss} className="text-slate-500 hover:text-white h-7 w-7 p-0 flex-shrink-0" data-testid="dismiss-trial-banner">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Usage Stats - always show compact version */}
        <div className="grid grid-cols-5 gap-2 mt-3">
          {stats.map(s => (
            <div key={s.label} className="bg-slate-800/60 rounded-lg p-2 text-center">
              <s.icon className={`w-4 h-4 mx-auto mb-1 ${accentColor}`} />
              <p className="text-white font-bold text-sm">{s.value}</p>
              <p className="text-slate-500 text-[10px] leading-tight">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Expand for conversion message */}
        {!showFull && (
          <Button variant="ghost" size="sm" onClick={() => setShowFull(true)} className={`mt-2 w-full ${accentColor} hover:bg-white/5 text-xs`} data-testid="expand-trial-banner">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> {t('trialSeeValue')}
          </Button>
        )}

        {showFull && (
          <div className="mt-3 p-3 bg-slate-800/80 rounded-lg border border-slate-700/50 space-y-2" data-testid="trial-conversion-detail">
            <div className="flex items-center gap-2">
              <TrendingUp className={`w-4 h-4 ${accentColor}`} />
              <p className="text-white text-sm font-medium">{t('trialValueTitle')}</p>
            </div>
            <p className="text-slate-300 text-xs leading-relaxed">
              {t('trialValueMsg')
                .replace('{products}', data.products)
                .replace('{customers}', data.customers)
                .replace('{sales}', data.sales)
                .replace('{revenue}', data.total_revenue?.toLocaleString())}
            </p>
            {data.annual_fee > 0 && (
              <p className="text-slate-400 text-xs">{t('trialAnnualFee')}: <span className="text-white font-medium">${data.annual_fee}/{t('subYear')}</span></p>
            )}
            <div className={`p-2 rounded-lg ${isExpired ? 'bg-red-500/10 border border-red-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
              <p className={`text-xs font-medium ${isExpired ? 'text-red-400' : 'text-amber-400'}`}>
                {t('trialContactAdmin')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
